import Joi from 'joi';
import { ObjectParams } from '../../types/ParamsTypes';
import {
  BookingCustomer,
  BookingEscort,
  BookingParams,
  BookingService,
} from '../../types/BookingTypes';
import { ObjectResponse, ServiceFieldType, SingleObject } from '../../types/ObjectTypes';
import { AxiosResponse } from 'axios';
import { getAxiosWithConfig } from '../Http/HttpService';
import { ExtendedDetailsRoute, ExtendedDetailsRouteObjects } from '../../types/RoutesTypes';
import { getArrayObjectsRouteParam } from '../Objects/ObjectsService';

export const createBooking = async (
  bookingParams: BookingParams,
): Promise<AxiosResponse<ObjectResponse>> => {
  try {
    const instance = getAxiosWithConfig();

    return instance.post('plugin', {
      plugin: 'booking',
      data: {
        module: 'insertObjectBooking',
        params: bookingParams,
      },
    });
  } catch (e) {
    throw new Error(e);
  }
};

export const getSalutations = (): Array<{ id: string; title: string }> => {
  return [
    { id: '', title: 'Bitte wählen' },
    { id: 'FEMALE', title: 'Frau' },
    { id: 'MALE', title: 'Herr' },
  ];
};

export const getCountries = (): Array<{ id: string; title: string }> => {
  return [
    { id: 'Deutschland', title: 'Deutschland' },
    { id: 'Österreich', title: 'Österreich' },
    { id: 'Schweiz', title: 'Schweiz' },
    { id: 'Sonstige', title: 'Sonstige' },
  ];
};

export const getNumberRanges = (min: number, max: number): Array<{ id: number; title: number }> => {
  const numberRanges = [];
  for (let i = min; i <= max; i++) {
    numberRanges.push({ id: i, title: i });
  }
  return numberRanges;
};

export const getChildrenAges = (): Array<{ id: number; title: string }> => {
  const numberRanges = [];
  for (let i = 0; i < 18; i++) {
    numberRanges.push({ id: i, title: `${i} Jahre alt` });
  }
  return numberRanges;
};

export const getAcceptedRegulationsFormValidationSchema = (): Joi.AnySchema => {
  return Joi.object().keys({
    acceptedTermsOfService: Joi.boolean().invalid(false).required().messages({
      'any.invalid': 'Bitte bestätigen Sie unsere  Allgemeine Geschäftsbedingungen',
      'any.required': 'Bitte bestätigen Sie unsere  Allgemeine Geschäftsbedingungen',
    }),
    acceptedPrivacyPolicy: Joi.boolean().invalid(false).required().messages({
      'any.invalid': 'Bitte stimmen Sie der Verarbeitung Ihrer Daten zu',
      'any.required': 'Bitte stimmen Sie der Verarbeitung Ihrer Daten zu',
    }),
  });
};

export const getBookingParamsPersonalDetailsFormValidationSchema = (): Joi.AnySchema => {
  return Joi.object().keys({
    customer: Joi.object().keys({
      Salutation: Joi.string().not('').required().messages({
        'string.empty': 'Bitte geben Sie eine Anrede an.',
        'any.required': 'Bitte geben Sie eine Anrede an.',
      }),
      Title: Joi.string().allow('').required(),
      Firstname: Joi.string().required().messages({
        'string.empty': 'Bitte geben Sie Ihren Vornamen an.',
        'any.required': 'Bitte geben Sie Ihren Vornamen an.',
      }),
      Lastname: Joi.string().required().messages({
        'string.empty': 'Bitte geben Sie Ihren Nachnamen an.',
        'any.required': 'Bitte geben Sie Ihren Nachnamen an.',
      }),
      Street: Joi.string().required().messages({
        'string.empty': 'Bitte geben Sie Ihre Strasse an.',
        'any.required': 'Bitte geben Sie Ihren Strasse an.',
      }),
      Postal: Joi.string().required().messages({
        'string.empty': 'Bitte geben Sie die Postleitzahl an.',
        'any.required': 'Bitte geben Sie die Postleitzahl an.',
      }),
      City: Joi.string().required().messages({
        'string.empty': 'Bitte geben Sie die Stadt an.',
        'any.required': 'Bitte geben Sie die Stadt an.',
      }),
      Country: Joi.string().required().messages({
        'string.empty': 'Bitte geben Sie Ihr Land an.',
        'any.required': 'Bitte geben Sie Ihr Land an.',
      }),
      Phone1: Joi.string().min(3).required().messages({
        'string.empty': 'Bitte geben Sie Ihre Telefonnummer an.',
        'string.min': 'Diese Telefonnummer ist zu kurz.',
      }),
      Email: Joi.string()
        .email({ tlds: { allow: false } })
        .required()
        .messages({
          'string.empty': 'Bitte geben eine Email Adresse an.',
          'string.email': 'Bitte geben eine gültige Email Adresse an.',
        }),
      Comment: Joi.string().allow('').required(),
    }),
  });
};

export const getPersonalDetailsFormBookingParams = (
  bookingParams: BookingParams,
): { [key: string]: any } => {
  return {
    customer: {
      Salutation: bookingParams.customer.Salutation,
      Title: bookingParams.customer.Title,
      Firstname: bookingParams.customer.Firstname,
      Lastname: bookingParams.customer.Lastname,
      Phone1: bookingParams.customer.Phone1,
      Email: bookingParams.customer.Email,
      Street: bookingParams.customer.Street,
      Postal: bookingParams.customer.Postal,
      City: bookingParams.customer.City,
      Country: bookingParams.customer.Country,
      Comment: bookingParams.customer.Comment,
    },
  };
};

export const validateAgesOfChildren = (value: number, helpers: Joi.CustomHelpers) => {
  const baseData = helpers.prefs.context?.baseData;
  const ages: number[] = [...helpers.state.ancestors[0]];
  const index: any = helpers.state?.path?.[1];
  // numberOfAdults + numberOfChildren === maximumPersons entspricht
  if (
    baseData &&
    ages.length > 0 &&
    typeof index === 'number' &&
    helpers.state.ancestors[1].numberOfAdults + helpers.state.ancestors[1].numberOfChildren >
    baseData.maximumPersons
  ) {
    ages.sort((a, b) => a - b);
    const maxAgeExtraBed1 = baseData.maxAgeExtraBed1;
    const maxAgeExtraBed2 = baseData.maxAgeExtraBed2;

    const firstChildAge = ages[0];
    const secondChildAge = ages[1];
    const minValue = Math.min(...ages);
    if (!!maxAgeExtraBed1 && !maxAgeExtraBed2 && minValue === value) {
      if (firstChildAge > maxAgeExtraBed1) {
        return helpers.error('any.maxAgeExtraBed1Exceeded');
      }
    } else if (!!maxAgeExtraBed1 && !!maxAgeExtraBed2) {
      if (
        (firstChildAge === value && value > maxAgeExtraBed1 && value > maxAgeExtraBed2) ||
        (secondChildAge === value && value > maxAgeExtraBed1 && value > maxAgeExtraBed2)
      ) {
        return helpers.error('any.maxAgeExtraBed2Exceeded');
      }
    }

    return value;
  } else {
    return value;
  }
};
export const getBookingParamsTravelFormValidationSchema = (object: SingleObject): Joi.AnySchema => {
  const sharedSchema = Joi.object().keys({
    services: Joi.array().required(),
  });
  const addExtraChild1: boolean = object.baseData.maxAgeExtraBed1 > 0;
  const addExtraChild2: boolean = object.baseData.maxAgeExtraBed2 > 0;
  return Joi.alternatives().try(
    Joi.object()
      .keys({
        numberOfAdults: Joi.number()
          .max(
            Joi.ref('numberOfChildren', {
              adjust: value =>
                object.baseData.maximumPersons -
                value +
                (addExtraChild1 ? 1 : 0) +
                (addExtraChild2 ? 1 : 0),
            }),
          )
          .min(
            Joi.ref('numberOfChildren', {
              adjust: value => object.baseData.minimumPersons - value,
            }),
          )
          .required()
          .messages({
            'number.max': 'Bitte beachten Sie auf die maximale Anzahl Personen',
            'number.min': 'Bitte beachten Sie auf die minimale Anzahl Personen',
          }),
        numberOfChildren: Joi.number().required(),
        agesOfChildren: Joi.array()
          .required()
          .items(
            Joi.custom(validateAgesOfChildren, 'validate ages of children').messages({
              'any.maxAgeExtraBed1Exceeded': `Das jüngste Kind darf nicht älter wie ${
                object.baseData.maxAgeExtraBed1
              } ${object.baseData.maxAgeExtraBed1 === 1 ? 'Jahr' : 'Jahre sein.'}`,
              'any.maxAgeExtraBed2Exceeded': `Die jüngsten 2 Kinder dürfen nicht älter wie ${object.baseData.maxAgeExtraBed1} und ${object.baseData.maxAgeExtraBed2} Jahre sein.`,
            }),
          )
          .length(Joi.ref('numberOfChildren'))
          .required(),
      })
      .concat(sharedSchema),
  );
};

export const getTravelFormBookingParams = (
  bookingParams: BookingParams,
): { [key: string]: any } => {
  return {
    numberOfAdults: bookingParams.numberOfAdults,
    numberOfChildren: bookingParams.numberOfChildren,
    agesOfChildren: bookingParams.agesOfChildren,
    services: bookingParams.services,
  };
};

export const getInitialBookingRouteParams = (routeParams: ExtendedDetailsRoute): ObjectParams => {
  let agesOfChildren = getArrayObjectsRouteParam('ages', routeParams?.agesOfChildren);

  return {
    Object: routeParams.id ? routeParams.id : '',
    fromDate: routeParams.fromDate ? routeParams.fromDate : '',
    toDate: routeParams.toDate ? routeParams.toDate : '',
    numberOfAdults: routeParams.numberOfAdults ? Number.parseInt(routeParams.numberOfAdults) : 2,
    numberOfChildren: routeParams.numberOfChildren
      ? Number.parseInt(routeParams.numberOfChildren)
      : 0,
    agesOfChildren: agesOfChildren,
    numberOfPets: routeParams.numberOfPets ? +routeParams.numberOfPets : 0,
  };
};

export const getInitialBookingParams = (
  object: SingleObject,
  objectParams: ObjectParams,
): BookingParams => {
  const bookingParams: BookingParams = {
    ignoreConditions: false,
    Type: 'BOOKING',
    Object: object.baseData.uniqueId,
    fromDate: objectParams.fromDate,
    toDate: objectParams.toDate,
    numberOfAdults: objectParams.numberOfAdults,
    numberOfChildren: objectParams.numberOfChildren,
    agesOfChildren: objectParams.agesOfChildren,
    numberOfPets: objectParams.numberOfPets,
    typeOfPets: [],
    services: getBookingServices(object),
    customer: getInitialCustomerParams(),
    customerGuest: [],
    escorts: [],
  };

  return { ...bookingParams };
};

export const getInitialEscort = (): BookingEscort => {
  return {
    Lastname: '',
    Firstname: '',
    Nationality: '',
    PassportNumber: '',
    DateOfBirth: '',
    DisabilityPercent: '',
    isEscort: true,
    EscortRequired: false,
    DisabilityPassportNumber: '',
  };
};

export const getInitialCustomerParams = (): BookingCustomer => {
  return {
    Salutation: '',
    Title: '',
    Lastname: '',
    Firstname: '',
    Nationality: '',
    Company: '',
    Street: '',
    Postal: '',
    City: '',
    Country: 'Deutschland',
    Phone1: '',
    DescriptionPhone1: '',
    Email: '',
    Language: '',
    PassportNumber: '',
    DateOfBirth: '',
    DisabilityPercent: '',
    isEscort: false,
    EscortRequired: false,
    DisabilityPassportNumber: '',
    numberOfPets: '',
    TypeOfPets: [],
    Comment: '',
  };
};

export const getBookingServices = (object: SingleObject): BookingService[] => {
  return object.servicesData
    .filter(service => !service.Mandatory)
    .map(service => {
      const bookingService: BookingService = {
        uniqueId: service.uniqueId,
        quantity: 0,
      };
      if (service.FieldType === ServiceFieldType.PER_DAY) {
        bookingService.numberOfDays = 0;
      }
      return bookingService;
    });
};

export const getBookingConfirmationIntroduction = (params: {
  Salutation: 'MALE' | 'FEMALE' | '';
  Title: string;
  Firstname: string;
  Lastname: string;
}): string => {
  const { Salutation, Title, Firstname, Lastname } = params;
  return `Sehr ${Salutation === 'MALE' ? 'geehrter' : 'geehrte'} ${
    Salutation === 'MALE' ? 'Herr' : 'Frau'
  }${Title.length > 0 ? ' ' + Title : ''} ${Firstname} ${Lastname},`;
};

export const getBookingRoute = (params: ObjectParams): string => {
  const {
    Object,
    fromDate,
    toDate,
    numberOfAdults,
    numberOfChildren,
    agesOfChildren,
    numberOfPets,
  } = params;
  const agesOfChildrenQuery = agesOfChildren?.length ? agesOfChildren.join(',') : 'no_ages';
  return `/bookings/${Object}/${numberOfAdults}/${numberOfChildren}/${agesOfChildrenQuery}/${numberOfPets}/${fromDate}/${toDate}`;
};

export const getMaxNumberOfChildren = (
  object: SingleObject,
  bookingParams: BookingParams,
): number => {
  const { baseData } = object;
  const { numberOfAdults } = bookingParams;
  const addExtraChild1: boolean = baseData.maxAgeExtraBed1 > 0;
  const addExtraChild2: boolean = baseData.maxAgeExtraBed2 > 0;
  return (
    baseData.maximumPersons - numberOfAdults + (addExtraChild1 ? 1 : 0) + (addExtraChild2 ? 1 : 0)
  );
};
